import { useState } from 'react'

import { G, ru, weightN } from '../../lib/physics'
import { Arrow, ArrowDefs, Choice, Predict, Slider, useFrames, зигзаг } from './common'

/**
 * Вес в лифте (урок 36): гиря на динамометре в движущемся лифте. Сила тяжести mg
 * не меняется, меняется вес P = m(g + a); в свободном падении P = 0 — невесомость.
 */

type Режим = 'покой' | 'равномерно' | 'разгон вверх' | 'разгон вниз' | 'падение'

const УСКОРЕНИЕ: Record<Режим, number> = {
  покой: 0,
  равномерно: 0,
  'разгон вверх': 3,
  'разгон вниз': -3,
  падение: -G,
}
const СКОРОСТЬ_0: Record<Режим, number> = { покой: 0, равномерно: 2, 'разгон вверх': 0, 'разгон вниз': 0, падение: 0 }

const PX_PER_N = 3.5 // стрелка силы
const PX_PER_N_SPRING = 2.5 // растяжение пружины на рисунке

export default function ElevatorLab() {
  const [режим, setРежим] = useState<Режим>('покой')
  const [m, setM] = useState(1)
  const [движ, setДвиж] = useState({ v: СКОРОСТЬ_0['покой'], сдвиг: 0, t: 0 })

  const a = УСКОРЕНИЕ[режим]
  const P = weightN(m, a)
  const fТяж = m * G

  useFrames((dt) => {
    setДвиж((p) => {
      // лифт ездит циклами по 3 с; стены «бегут» навстречу движению
      let { v, t } = p
      t += dt
      v += a * dt
      if (t > 3) {
        t = 0
        v = СКОРОСТЬ_0[режим]
      }
      return { v, t, сдвиг: (p.сдвиг + v * dt * 40) % 40 }
    })
  })

  const сменить = (r: Режим) => {
    setРежим(r)
    setДвиж({ v: СКОРОСТЬ_0[r], сдвиг: 0, t: 0 })
  }

  const пружина = 30 + P * PX_PER_N_SPRING
  const крюк = 70 + пружина
  const гиряY = крюк + 8
  const полосы: number[] = []
  for (let y = -40; y < 320; y += 40) полосы.push(y + движ.сдвиг)

  const больше = P > fТяж + 0.01
  const меньше = P < fТяж - 0.01
  const верный = P === 0 ? 3 : больше ? 0 : меньше ? 1 : 2

  return (
    <div className="card">
      <div className="lab-two">
        <svg className="lab-svg" viewBox="0 0 220 300" role="img" aria-label="Гиря на динамометре в лифте">
          <ArrowDefs />
          {/* шахта: полосы на стене бегут — лифт едет */}
          {полосы.map((y) => (
            <line key={y} x1={10} y1={y} x2={30} y2={y} className="ink-muted" strokeWidth={2} />
          ))}
          {полосы.map((y) => (
            <line key={`r${y}`} x1={190} y1={y} x2={210} y2={y} className="ink-muted" strokeWidth={2} />
          ))}
          <rect x={40} y={40} width={140} height={240} rx={4} className="fill-cabin ink" strokeWidth={2} />
          <line x1={110} y1={0} x2={110} y2={40} className="ink" strokeWidth={режим === 'падение' ? 0 : 2} />
          {режим === 'падение' && <text x={110} y={30} textAnchor="middle" className="lab-label heavy">трос оборвался</text>}
          {/* динамометр: корпус, пружина, крюк */}
          <rect x={98} y={44} width={24} height={24} rx={3} className="fill-muted" />
          <polyline points={зигзаг(110, 68, пружина)} className="ink-band" strokeWidth={2} fill="none" />
          <line x1={110} y1={68 + пружина} x2={110} y2={гиряY} className="ink" strokeWidth={2} />
          <rect x={92} y={гиряY} width={36} height={30} rx={4} className="fill-body ink" strokeWidth={1.5} />
          <text x={110} y={гиряY + 20} textAnchor="middle" className="lab-label">{ru(m, 1)} кг</text>
          <Arrow x1={104} y1={гиряY + 15} x2={104} y2={гиряY + 15 + fТяж * PX_PER_N} kind="heavy" label="F тяж" labelDx={-46} labelDy={0} />
          <Arrow x1={116} y1={гиряY} x2={116} y2={гиряY - P * PX_PER_N} kind="el" label="F упр" labelDx={6} labelDy={10} />
        </svg>

        <div>
          <div className="lab-big">
            Динамометр: <b>{ru(P, 1)} Н</b>
          </div>
          <div className="lab-readout lab-readout-col">
            <span className="heavy">Сила тяжести m·g = {ru(fТяж, 1)} Н — не меняется</span>
            <span className="el">Вес P = m·(g + a) = {ru(P, 1)} Н</span>
            <span>ускорение лифта a = {a > 0 ? '+' : ''}{ru(a, 0)} м/с² {a > 0 ? '(вверх)' : a < 0 ? '(вниз)' : ''}</span>
          </div>
          <div className="lab-caption">
            {P === 0
              ? 'Невесомость: гиря не растягивает пружину, хотя Земля её притягивает.'
              : больше
                ? 'Вес больше силы тяжести — перегрузка.'
                : меньше
                  ? 'Вес меньше силы тяжести.'
                  : 'Вес равен силе тяжести: лифт стоит или едет равномерно.'}
          </div>
        </div>
      </div>

      <Predict
        question={`Лифт: «${режим}». Что покажет динамометр?`}
        options={[`больше ${ru(fТяж, 0)} Н`, `меньше ${ru(fТяж, 0)} Н`, `ровно ${ru(fТяж, 0)} Н`, '0 Н']}
        right={верный}
        revealed
        resetKey={режим}
        explain="Динамометр показывает вес — силу, с которой гиря тянет пружину. Сила тяжести при этом та же: mg."
      />

      <div className="lab-sub">Лифт</div>
      <Choice
        options={[
          { value: 'покой', label: 'стоит' },
          { value: 'равномерно', label: 'едет равномерно' },
          { value: 'разгон вверх', label: 'разгоняется вверх' },
          { value: 'разгон вниз', label: 'разгоняется вниз' },
          { value: 'падение', label: 'трос оборвался' },
        ]}
        value={режим}
        onChange={сменить}
      />
      <Slider label="Масса гири" value={m} min={0.5} max={2} step={0.1} unit="кг" shown={ru(m, 1)} onChange={setM} />
      <p className="muted small">
        Вес — сила, с которой тело давит на опору или растягивает подвес. Он равен mg, только
        когда опора покоится или движется равномерно.
      </p>
    </div>
  )
}
